// frontend/src/hooks/useCan.ts — the per-project capability hook the nav
// (and, later, permission-gated buttons) gate through.
//
// Folds the two halves of "can this user see X":
//   - user-level `me.user.is_sysadmin`  (sysadmin sees everything everywhere)
//   - per-project `me.permissions[projectId]` (membership + is_project_admin +
//     the includes-expanded capability keys)
// into one `can(gate)` predicate, where a gate is a catalogue capability key OR
// one of the synthetic atoms:
//   - 'member' — any member of the project (membership-is-the-gate, per
//     ADR-003 / ANATOMY L4 — Home)
//   - 'admin'  — built-in project administration (project_admin or sysadmin;
//     no capability key — e.g. project settings)
//   - 'on-a-ticket' · 'on-a-team' — what the person has of their own in the
//     Work area with no key (`/me`'s `participates`: a ticket they are on, a
//     team they are on)
//   - 'namer:<level>' — the people key WITH that responsibility level (team ·
//     site · cluster · zone): the viewer may name chains there. `namer:team`
//     reads the teams it may name (ADR-083 §3 — the backend's twin is
//     teams/access `teamReachOf`); `namer:site` is a site's Manage
//     responsibilities gate (the server's whole word, the scope included, is
//     the read's — `can_assign`).
//
// `can` is wrapped in useCallback so its identity is stable while `me` is
// unchanged — consumers can pass it into the pure nav-gating functions in
// `config/nav.ts` without re-deriving menus every render.
//
// useCan is the frontend mirror of the backend `can()` decision point — it folds
// BOTH the capability check (lib/hasPermission) AND the entitlement gate (ADR-044,
// in the same order: entitlement BEFORE the admin short-circuit), so an off-module
// capability is hidden from EVERYONE, admins included. The set on `/me` is already
// includes-expanded + entitlement-filtered for non-admins; the explicit gate here
// closes the admin path (admins bypass the set).

import { useCallback } from 'react';
import { entitlementOf } from '@ems-app/schemas/permissions';
import { useMe } from './useMe.js';
import { hasPermission } from '../lib/hasPermission.js';
import type { ResponsibilityLevel } from '@ems-app/schemas/responsibilities/levels';
import type { NavGate } from '../config/nav.js';

export type Can = (gate: NavGate) => boolean;

export function useCan(projectId: string): Can {
  const { data: me } = useMe();
  const isSysadmin = me?.user.is_sysadmin ?? false;
  const perms = me?.permissions[projectId];

  return useCallback(
    (gate: NavGate): boolean => {
      // sysadmin is an implicit member + admin of every project (ADR-022)
      if (gate === 'member') return isSysadmin || perms !== undefined;
      if (gate === 'admin') return isSysadmin || perms?.is_project_admin === true;
      if (gate === 'on-a-ticket') return perms?.participates.tickets === true;
      if (gate === 'on-a-team') return perms?.participates.teams === true;
      if (gate.startsWith('namer:')) {
        const level = gate.slice('namer:'.length) as ResponsibilityLevel;
        return hasPermission(perms, isSysadmin, 'users:manage') && (isSysadmin || (perms?.assignableResponsibilityLevels ?? []).includes(level));
      }
      // Entitlement gate (mirror backend can() step 0): an off-module capability
      // is denied for everyone, admins included — before the admin short-circuit
      // inside hasPermission.
      const flag = entitlementOf(gate);
      if (flag !== undefined && !(perms?.entitlements ?? []).includes(flag)) return false;
      return hasPermission(perms, isSysadmin, gate);
    },
    [isSysadmin, perms],
  );
}
